/**
 * LegalConsentOverlay (BUG42) — read the Terms / Privacy legal document in an
 * IN-APP overlay rendered OVER the Create account screen, WITHOUT navigating.
 *
 * Why an overlay, not a link/new tab: the consent links used to be a `<Link>`
 * (SPA-navigated away → unmounted CreateAccountPage → dropped the entered
 * email/password/consent) and then a plain `<a target="_blank">` (a new tab that
 * the Android WebView never opens). An overlay keeps CreateAccountPage MOUNTED —
 * the form state (email / password / confirm / Turnstile / consent) survives —
 * and works identically on web and inside the native WebView. No router
 * navigation, no new tab, no Capacitor plugin.
 *
 * The body reuses the exact loading → error → `renderLegalMarkdown` sequence
 * `LegalDocPage` uses (same `fetchLegalDoc(slug)` fetch of `GET /api/legal/:slug`),
 * just inside the shared `Modal` (sheet variant) instead of a full page.
 *
 * The shared Modal supplies the dialog a11y that's the same for every overlay
 * (role="dialog" + aria-modal, aria-labelledby via `title`, Escape close,
 * overlay-click close, focus-into-panel on open, portal above the bottom nav,
 * token styling, safe-area reserve on the sheet, Android hardware-Back close).
 * This wrapper adds the two things Modal does not: body scroll-lock while open,
 * and FOCUS RETURN to the triggering consent button on close.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Modal } from '../../components';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { fetchLegalDoc, LEGAL_DOCS, type LegalSlug } from '../legal/content';
import { renderLegalMarkdown } from '../legal/markdown';

const statusStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-base)',
  lineHeight: 'var(--line-height-relaxed)',
  margin: 0,
};

const bodyStyle: CSSProperties = {
  // The sheet body already scrolls (Modal's sheet variant); this just gives the
  // rendered markdown its own vertical rhythm at the top of the scroller.
  display: 'block',
};

type FetchState =
  | { phase: 'loading' }
  | { phase: 'ready'; content: string }
  | { phase: 'error' };

export interface LegalConsentOverlayProps {
  /** Which document to show, or `null` when the overlay is closed. */
  slug: LegalSlug | null;
  /** Close the overlay. Focus returns to `triggerRef` if it is still on screen. */
  onClose: () => void;
  /**
   * The consent button that opened the overlay. Focus returns here on close so a
   * keyboard/AT user lands back on the control they pressed (WCAG 2.4.3).
   */
  triggerRef: React.RefObject<HTMLElement>;
}

/**
 * Render one published legal document inside a bottom-sheet dialog over the
 * current screen. `slug === null` means closed (renders nothing). The fetch runs
 * per-slug; the loading / error / ready states mirror LegalDocPage so a slow or
 * failed load never shows a blank sheet.
 */
export function LegalConsentOverlay({
  slug,
  onClose,
  triggerRef,
}: LegalConsentOverlayProps): JSX.Element | null {
  const open = slug !== null;
  const [state, setState] = useState<FetchState>({ phase: 'loading' });

  // Lock the page behind the sheet so the create-account form doesn't scroll
  // under the overlay (Modal itself doesn't lock body scroll).
  useBodyScrollLock(open);

  // Fetch the document whenever the open slug changes. Guards a stale resolve
  // (the user reopened with a different slug before the first fetch settled).
  useEffect(() => {
    if (slug === null) return undefined;
    let active = true;
    setState({ phase: 'loading' });
    fetchLegalDoc(slug)
      .then((doc) => {
        if (active) setState({ phase: 'ready', content: doc.content });
      })
      .catch(() => {
        if (active) setState({ phase: 'error' });
      });
    return () => {
      active = false;
    };
  }, [slug]);

  // Return focus to the triggering consent button on close. Modal moves focus
  // INTO the panel on open but never restores it; without this the focus would
  // be lost to <body> after the sheet closes.
  //
  // The parent derives `triggerRef` from the open slug, so by the close render
  // (slug → null) it has already flipped to its fallback. Snapshot the trigger's
  // ELEMENT while open, and restore focus to that captured element on close — so
  // closing the Privacy sheet returns to the Privacy button, not Terms.
  const wasOpen = useRef(false);
  const capturedTrigger = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      // Snapshot the trigger element while the sheet is open — this is the
      // control to return focus to once it closes.
      capturedTrigger.current = triggerRef.current;
      return;
    }
    if (wasOpen.current) {
      wasOpen.current = false;
      const trigger = capturedTrigger.current;
      capturedTrigger.current = null;
      if (trigger && document.contains(trigger)) trigger.focus();
    }
  }, [open, triggerRef]);

  if (slug === null) return null;

  const title = LEGAL_DOCS[slug].title;

  return (
    <Modal open onClose={onClose} variant="sheet" title={title}>
      <div style={bodyStyle} data-testid={`legal-consent-body-${slug}`}>
        {state.phase === 'loading' && (
          <p style={statusStyle} data-testid="legal-consent-loading">
            Loading {title}…
          </p>
        )}
        {state.phase === 'error' && (
          <p style={statusStyle} data-testid="legal-consent-error">
            We couldn’t load this document right now. Please check your connection
            and try again later.
          </p>
        )}
        {state.phase === 'ready' && renderLegalMarkdown(state.content)}
      </div>
    </Modal>
  );
}
